import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Billing from '@web-solutions/module-billing';
import Analytics from '@web-solutions/module-analytics';
import { PaymentSystem } from '@web-solutions/react-billing';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { selectPaymentSystem } from 'core/store/billing/selectors';

import { useStore } from '../store/store';
import { setupStripePayment } from '../helpers/setupStripePayment';

/**
 * Stripe deferred-email flow: when `collectEmailAfterPayment` is enabled and the
 * payment system is Stripe, create an anonymous Stripe customer (empty
 * POST /stripe/customers) as soon as the user opens the paywall /
 * personal-plan page. This authorizes billing with the returned token and loads
 * the subscription + onetime products, so checkout works exactly like the
 * regular flow but without asking for an email first. The email is collected
 * after payment on the `email-after-payment` page and attached to this customer
 * via POST /auth/email/attach.
 *
 * No-op unless `collectEmailAfterPayment` is enabled and Stripe is used.
 */
export function useStripeAnonymousCustomer(enabled: boolean = true) {
  const dispatch = useDispatch();
  // @ts-ignore — collectEmailAfterPayment lives on the core RemoteConfig type
  const { collectEmailAfterPayment } = useRemoteConfig();
  const paymentSystem = useSelector(selectPaymentSystem);
  const email = useStore((state) => state.email);

  const requestedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (paymentSystem !== PaymentSystem.STRIPE || !collectEmailAfterPayment) return;
    if (requestedRef.current) return;

    // The subscription flow (?flow=subscription&id=...) bootstraps Stripe itself
    // from /profile/{id}/email, so don't create an anonymous customer there.
    const params = new URLSearchParams(window.location.search);
    if (params.get('flow') === 'subscription') return;

    // Returning visitor / user already typed an email or has a customer token —
    // billing is (or will be) set up, don't create a second anonymous customer.
    if (email || Billing.stripeCustomerToken) {
      requestedRef.current = true;
      return;
    }

    requestedRef.current = true;

    (async () => {
      try {
        // Empty email -> POST /stripe/customers with an empty body (anonymous customer).
        await setupStripePayment({ email: '', dispatch, paymentSystem });
        Analytics.trackEvent('stripe_anonymous_customer', 'created');
      } catch (error) {
        // Allow a retry on the next render if the request failed.
        requestedRef.current = false;
        Analytics.trackEvent('stripe_anonymous_customer', 'error');
        console.error('[useStripeAnonymousCustomer] failed to create anonymous customer', error);
      }
    })();
  }, [enabled, collectEmailAfterPayment, paymentSystem, email, dispatch]);
}
